import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactMediaRecorder } from "react-media-recorder";
import { AppConfig } from "@/common/appConfig";
import { RecorderStatus } from "@/common/types/recorder";
import { underV3 } from "@/components/ui/v3/flagged";
import { getAudioTranscriptionHook } from "@/hooks/transcriber/useAudioTranscriptionFactory";
import { getAudioReactMediaRecorderOptions } from "@/hooks/transcriber/utils";
import { toast } from "@/hooks/use-toast";
import { useScreenWakeLock } from "@/hooks/useScreenWakeLock";
import {
  getSafeAudioConstraints,
  getSupportedMimeType,
} from "@/lib/mediaRecorder/utils";
import type { LocalRecordingCapture } from "@/lib/offline-recording/capture";
import {
  openRecordingStream,
  recordingAudioConstraints,
} from "@/lib/recordingMicrophone";
import { cn } from "@/lib/utils";
import { useOwnOfflineMessage } from "@/store/recorderOfflineMessage.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { LoadingSpinner } from "../loading-spinner";
import { OfflineRecordingBanner } from "../offline-recording/OfflineRecordingBanner";
import {
  type LocalCaptureControls,
  useLocalCapture,
} from "../offline-recording/useLocalCapture";
import {
  LiveTranscriptPausedNotice,
  RecorderNotices,
} from "./live-transcript-paused";
import RecorderUIInner from "./recorder-ui-inner";

/**
 * Longest wait for the realtime provider. Recording starts as soon as the
 * microphone is open (what is said meanwhile is held for the live
 * transcript); after this the audio is transcribed after Stop instead.
 */
export const PROVIDER_READY_TIMEOUT_MS = 10_000;

const canOpenMicrophone = (): boolean =>
  typeof navigator.mediaDevices?.getUserMedia === "function";

interface AudioRecorderUIProps {
  onFinished: (finished: boolean) => void;
  onRecordingComplete?: (blobUrl: string, transcription: string) => void;
  /** Also save the audio on this device while recording (offline recording). */
  offlineCapture?: boolean;
  onLocalCapture?: (capture: LocalRecordingCapture) => void;
  /**
   * The new-visit recorder (not an added recording): v3.2 layout and
   * recording instructions follow their flags.
   */
  newVisit?: boolean;
  /** New visit: discard the recording (the recorder asks first). */
  onDiscard?: () => void;
  /** v3.2 recorder: the note template chip. */
  noteSettings?: React.ReactNode;
  /** A recording added to a visit, in the v3.2 layout (see RecorderUIInner). */
  added?: { title: string; subtitle?: string; consent?: boolean };
  /**
   * v3.2 recorder: shown first in its notice slot while recording (the
   * practice consulta's script).
   */
  topNotice?: React.ReactNode;
}

/**
 * Mirrors the recorder's state into the on-device copy. A component so the
 * effects run when the render-prop values change.
 */
const LocalCaptureBridge = ({
  controls,
  stream,
  status,
}: {
  controls: LocalCaptureControls;
  stream: MediaStream | null;
  status: string;
}): null => {
  const { ensure, pause, resume } = controls;
  useEffect(() => {
    if (status === RecorderStatus.recording && stream) {
      ensure(stream);
      resume();
    } else if (status === RecorderStatus.paused) pause();
  }, [status, stream, ensure, pause, resume]);
  return null;
};

/** Keeps the screen on while recording (not while paused or stopped). */
const ScreenWakeLock = ({ active }: { active: boolean }): null => {
  useScreenWakeLock(active);
  return null;
};

const AudioRecorderUI: React.FC<AudioRecorderUIProps> = ({
  onRecordingComplete,
  offlineCapture = false,
  onLocalCapture,
  newVisit = false,
  onDiscard,
  noteSettings,
  added,
  topNotice,
}) => {
  const [autoStarted, setAutoStarted] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [finished, setFinished] = useState(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const completedBlobRef = useRef<string | null>(null);
  const finalAudioStreamRef = useRef<MediaStream | null>(null);
  const { isRealtimeEnabled } = useRecordingSettingsStore.getState();

  // The recorder appears when recording starts (Start recording, Add
  // recording, Start visit), so the microphone chosen right before is the one
  // read here, once, and every part of the recording (audio file, live
  // transcript, level meter, on-device copy) uses this one stream.
  const [deviceId] = useState(() =>
    useRecordingSettingsStore.getState().getRecordingDeviceId(),
  );
  const audioOptions = useMemo(() => {
    const { noiseReduction, noiseSuppression } =
      useRecordingSettingsStore.getState();
    return recordingAudioConstraints(
      deviceId,
      getSafeAudioConstraints(
        getAudioReactMediaRecorderOptions(noiseReduction, noiseSuppression),
      ),
    );
  }, [deviceId]);
  // The microphone, opened here with the chosen device required exactly (or
  // the default one with a notice when it is missing). Null while opening;
  // { stream: null } leaves opening to react-media-recorder with the same
  // constraints (no getUserMedia here, or a permission error to surface there).
  const [mic, setMic] = useState<{ stream: MediaStream | null } | null>(() =>
    canOpenMicrophone() ? null : { stream: null },
  );
  const micStreamRef = useRef<MediaStream | null>(null);

  const {
    setStream,
    transcription,
    finished: transcriptionFinished,
    finishSession: finishAudioTranscriptionSession,
    pauseStream,
    isProviderReady,
    requiresAudioRecovery,
    audioRecoveryReason,
    recoverAudio,
    liveTranscriptPaused,
  } = getAudioTranscriptionHook(isRealtimeEnabled)();
  const localCapture = useLocalCapture({
    enabled: offlineCapture,
    realtime: isRealtimeEnabled,
    onCapture: onLocalCapture,
  });
  // While recording, this recorder says what offline means for it (live
  // transcript paused, or saved on this device): no app-wide pill as well.
  useOwnOfflineMessage(!finished && (isRealtimeEnabled || offlineCapture));

  useEffect(() => {
    // Offline: live transcription cannot continue, so latch to "transcribe
    // the saved audio after Stop" now instead of waiting for the socket to
    // time out. The latch is the existing realtime -> batch recovery path.
    if (
      !offlineCapture ||
      localCapture.online ||
      !isRealtimeEnabled ||
      !autoStarted ||
      finished ||
      requiresAudioRecovery
    )
      return;
    recoverAudio();
  }, [
    offlineCapture,
    localCapture.online,
    isRealtimeEnabled,
    autoStarted,
    finished,
    requiresAudioRecovery,
    recoverAudio,
  ]);

  useEffect(() => {
    // A provider or socket that never answers: stop holding audio for it and
    // transcribe the full recording after Stop (the live transcript keeps
    // trying to connect meanwhile).
    if (!isRealtimeEnabled || isProviderReady || requiresAudioRecovery) return;
    const timer = window.setTimeout(recoverAudio, PROVIDER_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isRealtimeEnabled, isProviderReady, requiresAudioRecovery, recoverAudio]);

  useEffect(() => {
    if (
      !finished ||
      !mediaBlobUrl ||
      !isRealtimeEnabled ||
      transcriptionFinished ||
      requiresAudioRecovery
    )
      return;
    const timer = window.setTimeout(recoverAudio, 10000);
    return () => window.clearTimeout(timer);
  }, [
    finished,
    mediaBlobUrl,
    isRealtimeEnabled,
    transcriptionFinished,
    requiresAudioRecovery,
    recoverAudio,
  ]);

  useEffect(() => {
    if (!finished || !mediaBlobUrl || completedBlobRef.current === mediaBlobUrl)
      return;
    if (isRealtimeEnabled && !transcriptionFinished && !requiresAudioRecovery)
      return;
    completedBlobRef.current = mediaBlobUrl;
    onRecordingComplete?.(
      mediaBlobUrl,
      requiresAudioRecovery ? "" : transcription,
    );
  }, [
    finished,
    mediaBlobUrl,
    isRealtimeEnabled,
    transcriptionFinished,
    requiresAudioRecovery,
    transcription,
    onRecordingComplete,
  ]);

  useEffect(() => {
    if (!canOpenMicrophone()) return;
    let cancelled = false;
    const { noiseReduction, noiseSuppression } =
      useRecordingSettingsStore.getState();
    openRecordingStream({
      deviceId,
      processing: getSafeAudioConstraints(
        getAudioReactMediaRecorderOptions(noiseReduction, noiseSuppression),
      ),
    }).then(
      ({ stream, fellBack }) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }
        // The error tone: it shows the description and stays to be read.
        if (fellBack)
          toast({
            variant: "error",
            duration: 8000,
            title: t("recorderV32.micFallback.title"),
            description: t("recorderV32.micFallback.body"),
          });
        micStreamRef.current = stream;
        setMic({ stream });
      },
      () => {
        if (!cancelled) setMic({ stream: null });
      },
    );
    return (): void => {
      cancelled = true;
      micStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
      micStreamRef.current = null;
    };
  }, [deviceId]);

  useEffect(() => {
    return (): void => {
      if (finalAudioStreamRef.current) {
        finalAudioStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        finalAudioStreamRef.current = null;
      }
    };
  }, []);

  // Only the microphone is waited for: recording starts the moment it is
  // open, so nothing said right after Start is lost while the live
  // transcriber connects.
  if (!mic) {
    return <LoadingSpinner size={8} />;
  }

  const livePaused = isRealtimeEnabled && liveTranscriptPaused && !finished;
  // Why the note will be written from the full recording. While the live
  // transcript is paused, its own notice already covers a lost connection.
  const recoveryText =
    requiresAudioRecovery &&
    (audioRecoveryReason === "incomplete" || (!livePaused && !isProviderReady))
      ? audioRecoveryReason === "incomplete"
        ? t(
            "components.recorderInner.audioIncomplete",
            "Live transcription didn't capture all of the audio, so the note will be written from the full recording.",
          )
        : t(
            "components.recorderInner.audioRecovery",
            "Live transcription is unavailable (connection lost or the speech service did not respond). Recording continues on this device; after Stop and Save, the transcript will be generated from the full audio.",
          )
      : null;
  // The v3.2 recorder shows its notices in one slot under its top bar; the
  // classic recorder and the finished screen keep them above.
  const inSlot =
    AppConfig.enableRecorderV32 && (newVisit || !!added) && !finished;
  const statusNotices =
    inSlot && (livePaused || recoveryText) ? (
      <RecorderNotices livePaused={livePaused} recoveryText={recoveryText} />
    ) : undefined;
  const slotNotices =
    inSlot && topNotice ? (
      <div className="space-y-3">
        {topNotice}
        {statusNotices}
      </div>
    ) : (
      statusNotices
    );

  return (
    <>
      {offlineCapture && !finished && (
        <OfflineRecordingBanner
          {...localCapture.notices}
          // The live transcript notice already says the recording is saved.
          offline={localCapture.notices.offline && !livePaused}
        />
      )}
      {!inSlot && livePaused && <LiveTranscriptPausedNotice />}
      {!inSlot && recoveryText && (
        <p
          className={cn(
            "text-sm text-center",
            underV3(
              "mx-3 mt-3 self-stretch rounded-xl bg-v3-warn-tint px-4 py-3 text-left font-v3 text-[13.5px] leading-5 text-v3-warn-text",
            ),
          )}
          role="status"
        >
          {recoveryText}
        </p>
      )}
      <ReactMediaRecorder
        stopStreamsOnStop
        askPermissionOnMount={false}
        audio={audioOptions}
        customMediaStream={mic.stream}
        mediaRecorderOptions={{
          mimeType: getSupportedMimeType(),
          audioBitsPerSecond: 24000,
        }}
        render={({
          status,
          startRecording,
          stopRecording,
          pauseRecording,
          resumeRecording,
          mediaBlobUrl,
          previewStream,
          previewAudioStream,
        }) => {
          const recording = status === RecorderStatus.recording;
          if (!finalAudioStreamRef.current && previewAudioStream) {
            finalAudioStreamRef.current = previewAudioStream;
          }
          if (previewAudioStream && status === RecorderStatus.recording) {
            setStream(previewAudioStream);
          }
          if (status === RecorderStatus.paused) {
            pauseStream();
          }
          if (status === RecorderStatus.stopped && mediaBlobUrl) {
            setMediaBlobUrl(mediaBlobUrl);
          }

          // Wait for MediaRecorder blob and/or transcript:end — never show a blank pane.
          if (
            finished &&
            (!mediaBlobUrl ||
              (isRealtimeEnabled &&
                !transcriptionFinished &&
                !requiresAudioRecovery))
          ) {
            return <LoadingSpinner size={8} />;
          }

          return (
            <>
              <ScreenWakeLock active={recording && !finished} />
              {offlineCapture && (
                <LocalCaptureBridge
                  controls={localCapture}
                  status={status}
                  stream={previewAudioStream ?? null}
                />
              )}
              <RecorderUIInner
                autoStarted={autoStarted}
                finished={finished}
                isRealtimeEnabled={isRealtimeEnabled}
                mediaBlobUrl={mediaBlobUrl ?? null}
                mediaStream={finished ? previewStream : previewAudioStream}
                pauseRecording={pauseRecording}
                recordingTime={recordingTime}
                resumeRecording={resumeRecording}
                setAutoStarted={setAutoStarted}
                setFinished={setFinished}
                setRecordingTime={setRecordingTime}
                setShowConfirmation={setShowConfirmation}
                showConfirmation={showConfirmation}
                newVisit={newVisit}
                noteSettings={noteSettings}
                notice={slotNotices}
                added={added}
                onDiscard={onDiscard}
                startRecording={startRecording}
                status={status}
                stopRecording={() => {
                  // Close the on-device copy before the tracks stop.
                  void localCapture.stop();
                  // End STT first (does not stop MediaStream tracks), then finalize the blob.
                  finishAudioTranscriptionSession();
                  stopRecording();
                }}
              />
            </>
          );
        }}
      />
    </>
  );
};

export default AudioRecorderUI;
